import { useMemo } from 'react';
import * as React from 'react';

import TrashButton from '@/components/elements/Button/TrashButton';
import Counter from '@/components/elements/Form/Counter';
import OddsRange from '@/components/elements/Text/OddsRange';
import SetNumber from '@/components/elements/Text/SetNumber';
import { BetType } from '@/lib/apollo/types/gen/graphql';
import { isPossibleMulti } from '@/lib/race';
import {
  BetCountData,
  BetMethod,
  BettableData,
  BettableItem,
  IsMultiData,
  OddsData,
} from '@/modelTypes';

import Base from '../index';
import {
  Detail,
  Details,
  ListItem,
  SetNum,
  SetNumWrapper,
  SummaryWrapper,
  TicketList,
} from './style';
import TicketHeader from './TicketHeader';

const isMulti = (data: IsMultiData, method: BetMethod, type: BetType) => {
  if (method === BetMethod.Formation && isPossibleMulti(type)) {
    return data[method];
  }
  if (method === BetMethod.Wheel) {
    return data[method][type];
  }
  return false;
};

interface BetListProps {
  oddsData: OddsData;
  isBettable: boolean;
  isProcessing: boolean;
  bettableData: BettableData;
  setBettableData: React.Dispatch<React.SetStateAction<BettableData>>;
  setIsShowModal: (bool: boolean) => void;
  modifyBetDetailKey: string;
  betCountData: BetCountData;
  isMultiData: IsMultiData;
  setBetCountData: React.Dispatch<React.SetStateAction<BetCountData>>;
  addToBetPanel: () => void;
  cancelModifyTheBetDetail: () => void;
  betPanelTicketCount: number;
}

const BetList: React.FC<BetListProps> = ({
  oddsData,
  isBettable,
  isProcessing,
  bettableData,
  setBettableData,
  setIsShowModal,
  modifyBetDetailKey,
  betCountData,
  isMultiData,
  setBetCountData,
  addToBetPanel,
  cancelModifyTheBetDetail,
  betPanelTicketCount,
}) => {
  const handleChange = (
    val: number,
    betMethod: BetMethod,
    betType: BetType,
  ) => {
    setBetCountData(prev => {
      const newList = { ...prev };
      newList[betMethod][betType] = val;
      return newList;
    });
  };

  const bettableItemCount = (bettableItems: BettableItem[]) =>
    bettableItems.filter(item => !item.isRemoved).length;

  const validItems = (methodKey: BetMethod, typeKey: BetType) =>
    bettableData[methodKey][typeKey].filter(t => !t.isRemoved);

  const addable = useMemo(() => {
    return (
      Object.values(bettableData).some(method =>
        Object.values(method).some(bet => bet.length > 0),
      ) && !isProcessing
    );
  }, [bettableData, isProcessing]);

  const deleteSetNum = (
    setNum: number,
    betMethod: BetMethod,
    betType: BetType,
  ) => {
    setBettableData(prev => {
      const details = [...prev[betMethod][betType]].map(d => {
        const detail = { ...d };
        if (d.setNum === setNum) {
          detail.isRemoved = !detail.isRemoved;
        }
        return detail;
      });
      const newData = { ...prev };
      newData[betMethod][betType] = details;
      return newData;
    });
  };

  return (
    <Base
      isBettable={isBettable}
      addable={addable}
      setIsShowModal={setIsShowModal}
      modifyKey={modifyBetDetailKey}
      addToBetPanel={addToBetPanel}
      cancelModifyFn={cancelModifyTheBetDetail}
      ticketCount={betPanelTicketCount}
    >
      {addable && (
        <TicketList>
          {(Object.keys(bettableData) as BetMethod[]).map(methodKey =>
            (Object.keys(bettableData[methodKey]) as BetType[]).map(typeKey => (
              <React.Fragment key={`${methodKey}-${typeKey}`}>
                {Boolean(bettableData[methodKey][typeKey].length) && (
                  <ListItem>
                    <SummaryWrapper>
                      <TicketHeader
                        method={methodKey}
                        type={typeKey}
                        multi={isMulti(isMultiData, methodKey, typeKey)}
                      />
                      <Details>
                        <Detail>{`${bettableItemCount(
                          bettableData[methodKey][typeKey],
                        )}組`}</Detail>
                        <Detail>
                          {oddsData.exists ? (
                            <OddsRange
                              odds={oddsData.data}
                              setNums={validItems(methodKey, typeKey).map(
                                d => d.setNum,
                              )}
                              betType={typeKey}
                            />
                          ) : (
                            '未集計'
                          )}
                        </Detail>
                      </Details>
                      <Counter
                        value={betCountData[methodKey][typeKey]}
                        min={1}
                        max={999}
                        onChange={val => handleChange(val, methodKey, typeKey)}
                        disabled={modifyBetDetailKey !== ''}
                      />
                    </SummaryWrapper>
                    <SetNumWrapper>
                      {bettableData[methodKey][typeKey].map(d => (
                        <div key={`${d.setNum}-${methodKey}-${typeKey}`}>
                          <SetNum isRemoved={d.isRemoved}>
                            <SetNumber setNums={[d.setNum]} betType={typeKey} />
                            <div>
                              <span>
                                {oddsData.exists ? (
                                  <OddsRange
                                    odds={oddsData.data}
                                    setNums={[d.setNum]}
                                    betType={typeKey}
                                  />
                                ) : (
                                  '未集計'
                                )}
                              </span>
                              <TrashButton
                                isRemoved={d.isRemoved}
                                onClick={() =>
                                  deleteSetNum(d.setNum, methodKey, typeKey)
                                }
                                disabled={methodKey === BetMethod.Odds}
                              />
                            </div>
                          </SetNum>
                        </div>
                      ))}
                    </SetNumWrapper>
                  </ListItem>
                )}
              </React.Fragment>
            )),
          )}
        </TicketList>
      )}
    </Base>
  );
};

export default BetList;
